import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Lenis from "lenis";
import "@/App.css";
import { ShopProvider } from "@/context/ShopContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import DropListPopup from "@/components/SmsPopup";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Contact from "@/pages/Contact";
import OrderStatus from "@/pages/OrderStatus";
import CheckoutResult from "@/pages/CheckoutResult";
import Admin from "@/pages/Admin";
import Policy from "@/pages/Policy";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const SmoothScroll = ({ children }) => {
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (t) => { lenis.raf(t); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, [reduce]);
  return children;
};

const PageWipe = () => {
  const location = useLocation();
  const reduce = useReducedMotion();
  if (reduce) return null;
  return (
    <AnimatePresence mode="wait">
      <motion.div key={location.pathname + location.search} className="pointer-events-none fixed inset-0 z-[80]">
        <motion.div
          className="absolute inset-0 bg-ll-red"
          initial={{ scaleY: 1 }}
          animate={{ scaleY: 0 }}
          transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }}
          style={{ transformOrigin: "top" }}
        />
        <motion.div
          className="absolute inset-0 bg-ll-black"
          initial={{ scaleY: 1 }}
          animate={{ scaleY: 0 }}
          transition={{ duration: 0.35, delay: 0.08, ease: [0.76, 0, 0.24, 1] }}
          style={{ transformOrigin: "top" }}
        />
      </motion.div>
    </AnimatePresence>
  );
};

function App() {
  return (
    <ShopProvider>
      <BrowserRouter>
        <SmoothScroll>
        <ScrollToTop />
        <PageWipe />
        <div className="min-h-screen bg-ll-black text-ll-bone">
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/product/:slug" element={<ProductDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/order-status" element={<OrderStatus />} />
              <Route path="/order-status/:token" element={<OrderStatus />} />
              <Route path="/checkout/success" element={<CheckoutResult kind="success" />} />
              <Route path="/checkout/cancel" element={<CheckoutResult kind="cancel" />} />
              <Route path="/policies/:slug" element={<Policy />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
          <CartDrawer />
          <DropListPopup />
        </div>
        </SmoothScroll>
      </BrowserRouter>
    </ShopProvider>
  );
}

export default App;
